import {
  createTelemetryInterceptor,
  type TelemetryInterceptor,
} from '@smiledragon-corp/signoz-nextjs/axios';
import type { AxiosInstance } from 'axios';

// 이 모듈은 `Shared/network` barrel에 재수출하지 않는다 — 일부 테스트가 barrel을 통째로
// `vi.mock('Shared/network', ...)`로 대체하기 때문(기존 `Shared/network/errorInterceptor`가
// 딥 import인 것과 같은 이유). 사용처는 항상 이 파일을 직접 딥 import 한다.

/**
 * SDK 기본값은 opt-in(`NEXT_PUBLIC_OTEL_TRACEPARENT === 'true'`일 때만 활성)이지만
 * 이 앱은 opt-out이다 — prod env 관리 부담을 줄이려 default on.
 * 판정은 **오직 이 파일에서만** 한다. 호출처에 리터럴로 흩뿌리면 SDK의 반대 기본값이 새어든다.
 */
const isTraceparentEnabled = () => process.env.NEXT_PUBLIC_OTEL_TRACEPARENT !== 'false';

/**
 * breadcrumb 버퍼는 signoz-core의 realm 단위 모듈 전역 배열이다. Web Worker에서 기록해도
 * 메인 스레드의 reportClientError가 읽는 배열과 달라 리포트에 안 실린다 → 메인 스레드만 계측한다.
 * 판별 기준은 "오토뷰잉이냐"가 아니라 "메인 스레드냐"다.
 */
const isReportableRealm = () => typeof window !== 'undefined';

/**
 * libs-frontend `HttpClient`의 interceptors 배열에 넣는 형태.
 * 비활성이면 undefined — 생성자의 `forEach(n => n && ...)`가 통째로 skip한다.
 *
 * 비활성 시 `createTelemetryInterceptor({ enabled: false })`를 쓰지 마라. 그 플래그는
 * traceparent 주입만 끄고 **breadcrumb 기록은 계속된다** — 킬스위치가 되지 않는다.
 */
export const createClientTelemetryInterceptor = (): TelemetryInterceptor | undefined => {
  if (!isTraceparentEnabled() || !isReportableRealm()) return undefined;

  const telemetry = createTelemetryInterceptor({ enabled: true });
  const recordFailure = telemetry.responseInterceptorOnRejected;

  return {
    ...telemetry,
    /**
     * `config`가 없는 에러 = ① 요청이 나가기 전 request 인터셉터가 reject했거나
     * ② 앞선 response 인터셉터가 원본 `AxiosError`를 다른 타입으로 갈아치운 뒤다
     * (`ErrorInterceptor` → `NetworkError`/`UnknownError`, `AuthInterceptor` → `AuthorizationError`).
     * 어느 쪽이든 "그 요청의 breadcrumb"이 아니다.
     *
     * SDK는 가드 없이 `url: ''`·`traceId: undefined`로 기록하고, 서버 `acceptAPI`는 breadcrumb을
     * 뒤에서 앞으로 훑어 traceId·spanId가 둘 다 유효한 첫 항목을 고르므로 그 쓰레기 항목을 건너뛰고
     * **더 과거의 유효 trace**를 채택한다(오귀속). 기록하지 않는 편이 정직하다.
     */
    responseInterceptorOnRejected: (error: unknown) => {
      const hasRequestContext = Boolean(
        (error as { config?: { url?: string } } | null)?.config?.url,
      );
      if (!hasRequestContext || !recordFailure) return Promise.reject(error);
      return recordFailure(error);
    },
  };
};

/** 이미 계측된 인스턴스. SDK가 config.metadata를 merge가 아니라 `=`로 덮어써서 두 번 붙으면 startTime이 깨진다. */
const instrumented = new WeakSet<AxiosInstance>();

/**
 * raw AxiosInstance용 등록 헬퍼. 비활성 / Worker / 중복 호출이면 no-op.
 *
 * 등록 순서는 호출측 책임이다 — axios는 request를 LIFO, response를 FIFO로 실행하므로
 * 이 함수를 auth/error 인터셉터보다 **먼저** 부르면 telemetry가 request 최후 / response 최초로
 * 실행된다(= durationMs에 토큰 갱신 await가 안 섞이고, 에러가 변환되기 전 원본을 관측).
 */
export const attachClientTelemetry = (instance: AxiosInstance): void => {
  if (instrumented.has(instance)) return;
  const telemetry = createClientTelemetryInterceptor();
  if (!telemetry) return;
  instrumented.add(instance);
  instance.interceptors.request.use(
    telemetry.requestInterceptorOnFulfilled,
    telemetry.requestInterceptorOnRejected,
  );
  instance.interceptors.response.use(
    telemetry.responseInterceptorOnFulfilled,
    telemetry.responseInterceptorOnRejected,
  );
};
